import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calculator, ArrowLeft, Loader2, DollarSign, TrendingDown, BarChart3, PiggyBank, Fuel, Shield, Wrench, Home, Tent } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTheme } from '../context/ThemeContext';
import ToolSalesGate from '../components/ToolSalesGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CostCalculatorPage = (props) => {
  const params = useParams();
  const sessionId = props.sessionId ?? params.sessionId;
  if (props.skipGate) {
    return <CostCalculatorContent sessionId={sessionId} backTo={props.backTo} backLabel={props.backLabel} />;
  }
  return (
    <ToolSalesGate sessionId={sessionId} toolName="cost_calculator">
      <CostCalculatorContent sessionId={sessionId} />
    </ToolSalesGate>
  );
};

const CostCalculatorContent = ({ sessionId, backTo, backLabel }) => {
  const { isDark } = useTheme();
  const [form, setForm] = useState({
    purchase_price: '', rv_type: 'travel_trailer', financing: false,
    down_payment: '', loan_term_years: '10', interest_rate: '6.5',
    years_to_calculate: '5', miles_per_year: '5000', storage_needed: false, full_time: false,
    credit_score: '', monthly_income: '', monthly_debt_payments: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);

  const aprForScore = (s) =>
    s >= 780 ? 6.49 : s >= 720 ? 6.99 : s >= 690 ? 7.99 : s >= 660 ? 9.49 : s >= 620 ? 11.99 : s >= 580 ? 14.99 : 17.99;

  const handleCreditScore = (v) => {
    const score = parseInt(v, 10);
    setForm(f => ({
      ...f,
      credit_score: v,
      interest_rate: score >= 300 && score <= 850 ? String(aprForScore(score)) : f.interest_rate,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.purchase_price) { toast.error('Please enter a purchase price'); return; }
    setLoading(true);
    try {
      const payload = {
        purchase_price: parseFloat(form.purchase_price), rv_type: form.rv_type,
        financing: form.financing, down_payment: form.down_payment ? parseFloat(form.down_payment) : 0,
        loan_term_years: parseInt(form.loan_term_years), interest_rate: parseFloat(form.interest_rate),
        years_to_calculate: parseInt(form.years_to_calculate),
        miles_per_year: parseInt(form.miles_per_year),
        storage_needed: form.storage_needed, full_time: form.full_time,
        credit_score: form.credit_score ? parseInt(form.credit_score) : null,
        monthly_income: form.monthly_income ? parseFloat(form.monthly_income) : null,
        monthly_debt_payments: form.monthly_debt_payments ? parseFloat(form.monthly_debt_payments) : null,
      };
      const res = await axios.post(`${API}/tools/cost-calculator`, payload);
      setResult(res.data);
      setSelectedYear(null);
    } catch {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const bg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? '#141420' : '#fff';
  const border = isDark ? '#1e1e30' : '#e5e7eb';
  const textPrimary = isDark ? '#f0f0f5' : '#111827';
  const textSecondary = isDark ? '#8b8ba3' : '#6b7280';
  const accent = '#22c55e';

  return (
    <div data-testid="cost-calculator-page" style={{ background: bg, minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <div style={{ background: isDark ? '#101018' : '#fff', borderBottom: `1px solid ${border}`, padding: '16px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to={backTo || (sessionId ? `/vip/${sessionId}/dashboard` : '/')} style={{ color: textSecondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={18} /> {backLabel || (sessionId ? 'VIP Dashboard' : 'Home')}
          </Link>
          <span style={{ color: textSecondary }}>/</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calculator size={18} style={{ color: accent }} />
            <span style={{ color: accent, fontWeight: 700, fontSize: 15 }}>Cost Calculator</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `${accent}15`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: accent, marginBottom: 12 }}>
            <Calculator size={28} />
          </div>
          <h1 style={{ color: textPrimary, fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em' }}>Total Cost of Ownership</h1>
          <p style={{ color: textSecondary, fontSize: 15, maxWidth: 500, margin: '8px auto 0' }}>
            See the REAL cost of owning an RV — not just the sticker price.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: result ? 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))' : 'min(100%, 500px)', gap: 24, justifyContent: 'center', alignItems: 'start' }}>
          {/* Form */}
          <form onSubmit={handleSubmit} data-testid="cost-calc-form" style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Purchase Price *</Label>
                <Input data-testid="calc-price" type="number" value={form.purchase_price} onChange={e => setForm(f => ({...f, purchase_price: e.target.value}))} placeholder="75000" style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
              </div>
              <div>
                <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>RV Type</Label>
                <select data-testid="calc-type" value={form.rv_type} onChange={e => setForm(f => ({...f, rv_type: e.target.value}))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}>
                  <option value="class_a">Class A Motorhome</option>
                  <option value="class_b">Class B (Camper Van)</option>
                  <option value="class_c">Class C Motorhome</option>
                  <option value="travel_trailer">Travel Trailer</option>
                  <option value="fifth_wheel">Fifth Wheel</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Years to Calculate</Label>
                  <select data-testid="calc-years" value={form.years_to_calculate} onChange={e => setForm(f => ({...f, years_to_calculate: e.target.value}))}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}>
                    {[1,2,3,4,5,7,10].map(y => <option key={y} value={y}>{y} year{y>1?'s':''}</option>)}
                  </select>
                </div>
                <div>
                  <Label style={{ color: textSecondary, fontSize: 12, marginBottom: 4, display: 'block' }}>Miles / Year</Label>
                  <Input data-testid="calc-miles" type="number" value={form.miles_per_year} onChange={e => setForm(f => ({...f, miles_per_year: e.target.value}))} style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }} />
                </div>
              </div>

              {/* Financing Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
                <input data-testid="calc-financing" type="checkbox" checked={form.financing} onChange={e => setForm(f => ({...f, financing: e.target.checked}))} style={{ width: 18, height: 18, accentColor: accent }} />
                <Label style={{ color: textPrimary, fontSize: 14, cursor: 'pointer' }} onClick={() => setForm(f => ({...f, financing: !f.financing}))}>I'm financing this purchase</Label>
              </div>

              {form.financing && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb', border: `1px solid ${border}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    <div>
                      <Label style={{ color: textSecondary, fontSize: 11, display: 'block' }}>Down Payment</Label>
                      <Input data-testid="calc-down" type="number" value={form.down_payment} onChange={e => setForm(f => ({...f, down_payment: e.target.value}))} placeholder="15000" style={{ background: 'transparent', borderColor: border, color: textPrimary, fontSize: 13, padding: '4px 8px' }} />
                    </div>
                    <div>
                      <Label style={{ color: textSecondary, fontSize: 11, display: 'block' }}>Loan Term (yr)</Label>
                      <Input data-testid="calc-term" type="number" value={form.loan_term_years} onChange={e => setForm(f => ({...f, loan_term_years: e.target.value}))} style={{ background: 'transparent', borderColor: border, color: textPrimary, fontSize: 13, padding: '4px 8px' }} />
                    </div>
                    <div>
                      <Label style={{ color: textSecondary, fontSize: 11, display: 'block' }}>Rate (%)</Label>
                      <Input data-testid="calc-rate" type="number" step="0.1" value={form.interest_rate} onChange={e => setForm(f => ({...f, interest_rate: e.target.value}))} style={{ background: 'transparent', borderColor: border, color: textPrimary, fontSize: 13, padding: '4px 8px' }} />
                    </div>
                  </div>
                  <div>
                    <Label style={{ color: textSecondary, fontSize: 11, display: 'block' }}>Credit Score (300–850)</Label>
                    <Input data-testid="calc-credit-score" type="number" min="300" max="850" value={form.credit_score} onChange={e => handleCreditScore(e.target.value)} placeholder="720" style={{ background: 'transparent', borderColor: border, color: textPrimary, fontSize: 13, padding: '4px 8px' }} />
                    <p style={{ color: textSecondary, fontSize: 10, marginTop: 3 }}>Auto-fills a typical RV loan rate for your score — you can still adjust it.</p>
                  </div>
                </div>
              )}

              {/* Affordability (DTI) */}
              <div style={{ padding: 12, borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb', border: `1px solid ${border}` }}>
                <p style={{ color: textPrimary, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Affordability check <span style={{ color: textSecondary, fontWeight: 400 }}>(optional)</span></p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <Label style={{ color: textSecondary, fontSize: 11, display: 'block' }}>Gross Monthly Income</Label>
                    <Input data-testid="calc-income" type="number" value={form.monthly_income} onChange={e => setForm(f => ({...f, monthly_income: e.target.value}))} placeholder="7500" style={{ background: 'transparent', borderColor: border, color: textPrimary, fontSize: 13, padding: '4px 8px' }} />
                  </div>
                  <div>
                    <Label style={{ color: textSecondary, fontSize: 11, display: 'block' }}>Monthly Debt Payments</Label>
                    <Input data-testid="calc-debts" type="number" value={form.monthly_debt_payments} onChange={e => setForm(f => ({...f, monthly_debt_payments: e.target.value}))} placeholder="1800" style={{ background: 'transparent', borderColor: border, color: textPrimary, fontSize: 13, padding: '4px 8px' }} />
                  </div>
                </div>
                <p style={{ color: textSecondary, fontSize: 10, marginTop: 6 }}>Car loans, mortgage/rent, cards, etc. We'll show your debt-to-income ratio with the RV payment added.</p>
              </div>

              {/* Toggles */}
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: textPrimary, fontSize: 13, cursor: 'pointer' }}>
                  <input data-testid="calc-storage" type="checkbox" checked={form.storage_needed} onChange={e => setForm(f => ({...f, storage_needed: e.target.checked}))} style={{ accentColor: accent }} />
                  Need storage
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: textPrimary, fontSize: 13, cursor: 'pointer' }}>
                  <input data-testid="calc-fulltime" type="checkbox" checked={form.full_time} onChange={e => setForm(f => ({...f, full_time: e.target.checked}))} style={{ accentColor: accent }} />
                  Full-time RVing
                </label>
              </div>
            </div>

            <Button data-testid="calc-submit" type="submit" disabled={loading}
              style={{ width: '100%', marginTop: 20, background: accent, color: '#fff', fontWeight: 600, border: 'none', height: 44 }}>
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" style={{ marginRight: 8 }} /> Calculating...</> : 'Calculate Total Cost'}
            </Button>
          </form>

          {/* Results */}
          {result && (
            <div data-testid="cost-results" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Affordability & Credit */}
              {(result.affordability || result.credit_profile) && (
                <div data-testid="affordability-card" style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 20 }}>
                  <h3 style={{ color: textPrimary, fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Financing Fit</h3>
                  {result.credit_profile && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: result.affordability ? 14 : 0 }}>
                      <div>
                        <p style={{ color: textSecondary, fontSize: 11 }}>Credit Tier</p>
                        <p style={{ color: textPrimary, fontSize: 15, fontWeight: 700 }} data-testid="credit-tier">{result.credit_profile.tier}</p>
                      </div>
                      <div>
                        <p style={{ color: textSecondary, fontSize: 11 }}>Typical RV Loan APR</p>
                        <p style={{ color: '#6366f1', fontSize: 15, fontWeight: 700 }}>{result.credit_profile.estimated_apr}%</p>
                      </div>
                      <div>
                        <p style={{ color: textSecondary, fontSize: 11 }}>APR Used in This Estimate</p>
                        <p style={{ color: textPrimary, fontSize: 15, fontWeight: 700 }}>{result.credit_profile.apr_used}%</p>
                      </div>
                    </div>
                  )}
                  {result.affordability && (() => {
                    const a = result.affordability;
                    const verdictColor = { excellent: '#22c55e', good: '#0ea5e9', warning: '#f59e0b', danger: '#ef4444' }[a.verdict_level] || textSecondary;
                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                          <div style={{ padding: '10px 12px', borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb' }}>
                            <p style={{ color: textSecondary, fontSize: 11 }}>DTI Today</p>
                            <p style={{ color: textPrimary, fontSize: 18, fontWeight: 800 }} data-testid="dti-before">{a.dti_before_pct}%</p>
                          </div>
                          <div style={{ padding: '10px 12px', borderRadius: 8, background: `${verdictColor}10`, border: `1px solid ${verdictColor}40` }}>
                            <p style={{ color: textSecondary, fontSize: 11 }}>DTI With RV Payment</p>
                            <p style={{ color: verdictColor, fontSize: 18, fontWeight: 800 }} data-testid="dti-after">{a.dti_with_rv_pct}%</p>
                          </div>
                          <div style={{ padding: '10px 12px', borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb' }}>
                            <p style={{ color: textSecondary, fontSize: 11 }}>Max Payment @ 43% DTI</p>
                            <p style={{ color: textPrimary, fontSize: 18, fontWeight: 800 }}>${a.max_affordable_payment.toLocaleString()}/mo</p>
                          </div>
                          {a.suggested_max_price != null && (
                            <div style={{ padding: '10px 12px', borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb' }}>
                              <p style={{ color: textSecondary, fontSize: 11 }}>Suggested Max RV Price</p>
                              <p style={{ color: '#22c55e', fontSize: 18, fontWeight: 800 }} data-testid="suggested-max-price">${a.suggested_max_price.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        <p style={{ color: verdictColor, fontSize: 13, fontWeight: 600, marginTop: 10 }} data-testid="dti-verdict">{a.verdict}</p>
                        <p style={{ color: textSecondary, fontSize: 11, marginTop: 4 }}>{a.note}</p>
                      </>
                    );
                  })()}
                </div>
              )}
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                <SummaryCard icon={<DollarSign size={20} />} label="True Cost of Ownership" value={`$${result.summary.true_cost_of_ownership.toLocaleString()}`} sub={`Over ${result.summary.years_calculated} years`} color="#ef4444" isDark={isDark} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
                <SummaryCard icon={<PiggyBank size={20} />} label="Average Monthly Cost" value={`$${result.summary.average_monthly_cost.toLocaleString()}`} sub="All expenses included" color={accent} isDark={isDark} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
                <SummaryCard icon={<TrendingDown size={20} />} label="Total Depreciation" value={`$${result.summary.total_depreciation.toLocaleString()}`} sub={`Value at end: $${result.summary.rv_value_at_end.toLocaleString()}`} color="#f59e0b" isDark={isDark} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
                {result.financing && (
                  <SummaryCard icon={<BarChart3 size={20} />} label="Total Interest Paid" value={`$${result.summary.total_interest_paid.toLocaleString()}`} sub={`$${result.summary.monthly_payment.toLocaleString()}/mo payment`} color="#6366f1" isDark={isDark} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
                )}
              </div>

              {/* Year-by-Year Chart */}
              <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
                <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Year-by-Year Breakdown</h3>
                {/* Visual Bar Chart */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180, padding: '0 8px', marginBottom: 20 }}>
                  {result.yearly_breakdown.map((yr, i) => {
                    const maxTotal = Math.max(...result.yearly_breakdown.map(y => y.year_total));
                    const height = (yr.year_total / maxTotal) * 150;
                    const isSelected = selectedYear === i;
                    return (
                      <div key={yr.year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }} onClick={() => setSelectedYear(isSelected ? null : i)}>
                        <p style={{ color: textSecondary, fontSize: 11, marginBottom: 4 }}>${(yr.year_total / 1000).toFixed(0)}k</p>
                        <div style={{
                          width: '100%', maxWidth: 60, height, borderRadius: '6px 6px 0 0',
                          background: isSelected ? accent : `${accent}50`,
                          transition: 'all 0.2s',
                          border: isSelected ? `2px solid ${accent}` : '2px solid transparent',
                        }} />
                        <p style={{ color: isSelected ? accent : textSecondary, fontSize: 12, fontWeight: isSelected ? 700 : 400, marginTop: 6 }}>Yr {yr.year}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Selected Year Detail */}
                {selectedYear !== null && (
                  <div style={{ padding: 16, borderRadius: 10, background: isDark ? '#1a1a28' : '#f9fafb', border: `1px solid ${border}` }}>
                    <h4 style={{ color: textPrimary, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Year {result.yearly_breakdown[selectedYear].year} Detail</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                      {[
                        { label: 'Loan', value: result.yearly_breakdown[selectedYear].loan_payments, icon: <BarChart3 size={12} />, color: '#6366f1' },
                        { label: 'Insurance', value: result.yearly_breakdown[selectedYear].insurance, icon: <Shield size={12} />, color: '#0ea5e9' },
                        { label: 'Fuel', value: result.yearly_breakdown[selectedYear].fuel, icon: <Fuel size={12} />, color: '#f59e0b' },
                        { label: 'Maintenance', value: result.yearly_breakdown[selectedYear].maintenance, icon: <Wrench size={12} />, color: '#ef4444' },
                        { label: 'Storage', value: result.yearly_breakdown[selectedYear].storage, icon: <Home size={12} />, color: '#8b5cf6' },
                        { label: 'Camping', value: result.yearly_breakdown[selectedYear].campground, icon: <Tent size={12} />, color: '#22c55e' },
                      ].filter(item => item.value > 0).map(item => (
                        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: `${item.color}08` }}>
                          <span style={{ color: item.color }}>{item.icon}</span>
                          <div>
                            <p style={{ color: textSecondary, fontSize: 11 }}>{item.label}</p>
                            <p style={{ color: textPrimary, fontSize: 14, fontWeight: 600 }}>${item.value.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, background: `${accent}10`, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: textSecondary, fontSize: 13 }}>Year Total</span>
                      <span style={{ color: accent, fontWeight: 700, fontSize: 15 }}>${result.yearly_breakdown[selectedYear].year_total.toLocaleString()}</span>
                    </div>
                    <div style={{ marginTop: 6, padding: '8px 12px', borderRadius: 8, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: textSecondary, fontSize: 13 }}>RV Value</span>
                      <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 14 }}>${result.yearly_breakdown[selectedYear].rv_value.toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {selectedYear === null && <p style={{ color: textSecondary, fontSize: 13, textAlign: 'center' }}>Click a year bar for detailed breakdown</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon, label, value, sub, color, isDark, border, textPrimary, textSecondary }) => (
  <div style={{ background: isDark ? '#141420' : '#fff', borderRadius: 14, border: `1px solid ${border}`, padding: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <div style={{ color, opacity: 0.8 }}>{icon}</div>
      <span style={{ color: textSecondary, fontSize: 12, fontWeight: 500 }}>{label}</span>
    </div>
    <p style={{ color: textPrimary, fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{value}</p>
    <p style={{ color: textSecondary, fontSize: 12, marginTop: 4 }}>{sub}</p>
  </div>
);

export default CostCalculatorPage;
export { CostCalculatorContent };
